import React from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import LinearProgress from '@mui/material/LinearProgress';
import InputAdornment from '@mui/material/InputAdornment';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import imageCompression from 'browser-image-compression';
import moment from 'moment';

import firebase from '../../utilities/firebase';
import store from '../../utilities/store';
import { StateManager } from '../../utilities/stateManager';
import usePermissions from '../../utilities/usePermissions';
import { StackedDialog } from '../../utilities/ModalStackContext';
import { SalesCreditEditor } from '../SalesCreditDialog';
import {
  CREDIT_SOURCE,
  creditsAreValid,
  currentUserCredit,
  normalizeCredits,
  singleCredit,
  useSalesTeam,
} from '../../utilities/salesCredits';

// Screenshots off a phone are multi-MB; shrink them before upload. Matches FileUploader.
const COMPRESS_OPTS = { maxSizeMB: 2, maxWidthOrHeight: 2560, useWebWorker: true };

/**
 * The only write path for a car's NTO amounts.
 *
 * NTO is what the consignor agreed to be paid, so it is not a free-text field:
 * every change is recorded in `cars.nto_history` in the same write as the amount,
 * which means an amount can never move without leaving a trail. That history is
 * also what the consignment rep's NTO-reduction commission is calculated from.
 *
 * The dialog does two different jobs:
 *
 *   1. SET the first NTO on a car that has none. This is intake, not a renegotiation.
 *      Nothing exists to prove yet — the consignment agreement is generated FROM this
 *      amount, so demanding proof up front would deadlock the rep who needs the
 *      paperwork. Proof is optional here, and the amount creates the NTO payable.
 *   2. CHANGE an NTO that already exists. The consignor already agreed to a figure, so
 *      proof of the new agreement is required of everyone but admins.
 *
 * Which amount a change edits follows the deal, not the calendar:
 *   - no deal yet (intake/marketing/active) → the original `nto` changes
 *   - deal signed (`success`) or funded (`sold`) → only `updated_nto` changes
 *
 * Admins override that: they pick either amount in any status, because a wrong
 * original NTO on a sold car still has to be correctable. The override is flagged on
 * the history entry so a later reader can tell it did not follow the normal rules.
 *
 * The original NTO is the cost basis for sales performance: gross profit, deal margins
 * and the rep bonus average all price the car from the `purchases/{stock}-NTO` doc, and
 * only an original-NTO change rewrites it. An Updated NTO changes what we pay the
 * consignor, not what the deal is measured against.
 *
 * Proof is uploaded to the Document Library, linked to the car, and its id is stored on
 * the history entry, so any amount in the history can be traced back to what backs it.
 */
export default function UpdateNTODialog({ open, onClose, car = {} }) {
  const { isAdmin } = usePermissions();
  const stockNumber = car.stock;

  // `success` = a deal exists; `sold` = that deal is funded and the NTO is paid.
  // Either way the original NTO is settled with the consignor, so a renegotiation
  // from here is an Updated NTO.
  const isSold = car.status === 'sold';
  const hasDeal = isSold || car.status === 'success';
  const defaultField = hasDeal ? 'updated_nto' : 'nto';

  const original = Number(car.nto) || 0;
  // A car with no NTO and no deal is being set up, not renegotiated. Only the original
  // amount makes sense here, so there is nothing for an admin to choose between.
  const isInitial = !original && !hasDeal;

  const [field, setField] = React.useState(defaultField);
  const isOverride = !isInitial && field !== defaultField;
  const fieldLabel = field === 'updated_nto' ? 'Updated NTO' : 'Original NTO';

  // An Updated NTO that was never set starts from the original — that is the amount
  // being renegotiated.
  const amountFor = f => (f === 'updated_nto' ? (Number(car.updated_nto) || original) : original);
  const current = amountFor(field);

  const [amount, setAmount] = React.useState('');
  const [proof, setProof] = React.useState(null); // { id, name }
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  // Who earns the reduction. Defaults to the person recording it — negotiating the
  // consignor down is the work being paid for, and that is usually whoever is in this
  // dialog — falling back to the consignment rep, who is who the old calculation paid.
  const [credits, setCredits] = React.useState([]);
  const salesTeam = useSalesTeam();

  React.useEffect(() => {
    if (open) {
      setField(defaultField);
      setAmount(amountFor(defaultField) ? String(amountFor(defaultField)) : '');
      setProof(null);
      setUploading(false);
      setProgress(0);
      setSaving(false);
      setCredits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Seeded in its own effect because the team list arrives asynchronously. Only fills a
  // blank, so it never overwrites a pick the user has already made.
  React.useEffect(() => {
    if (!open) return;
    setCredits(prev => {
      if (prev.length) return prev;
      const mine = currentUserCredit(salesTeam);
      if (mine.length) return mine;
      const rep = salesTeam.find(t => t.name === car.consign_rep);
      return rep ? singleCredit(rep) : singleCredit({ name: car.consign_rep });
    });
  }, [open, salesTeam, car.consign_rep]);

  const parsed = parseFloat(amount);
  const valid = Number.isFinite(parsed) && parsed >= 0;
  const changed = valid && parsed !== current;
  const delta = valid ? current - parsed : 0;
  // On a first set there is no agreed figure to contradict — the paperwork comes after.
  const proofRequired = !isAdmin() && !isInitial;
  // Only a reduction pays anybody, so that is the only change worth crediting.
  const isReduction = changed && !isInitial && delta > 0;
  const creditsIncomplete = credits.some(row => !row.rep_id && !row.rep_name);
  const creditsOk = !isReduction || (!creditsIncomplete && creditsAreValid(credits));
  const canSave = changed && (!proofRequired || !!proof) && creditsOk;

  // Switching amounts re-seeds the input with what that amount is now, so an admin
  // never saves the other figure by accident.
  const pickField = (next) => {
    setField(next);
    setAmount(amountFor(next) ? String(amountFor(next)) : '');
  };

  // Proof uploads as soon as it is chosen so the rep sees it land before saving. It
  // is linked to the car, so it stays useful on the car's Files tab even if the
  // amount change is then abandoned.
  const attachProof = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;

    setUploading(true);
    setProgress(0);
    try {
      let toUpload = file;
      if (file.type && file.type.startsWith('image/')) {
        try {
          toUpload = await imageCompression(file, COMPRESS_OPTS);
        } catch (err) {
          console.warn('Proof compression failed, uploading original', err);
          toUpload = file;
        }
      }
      const uploaded = await store.uploadLibraryFile(
        toUpload,
        {
          category: 'other',
          tags: ['nto-proof'],
          links: [{ type: 'car', id: stockNumber, label: stockNumber }],
        },
        setProgress,
      );
      setProof({ id: uploaded.id, name: uploaded.name });
    } catch (err) {
      console.error('Error uploading NTO proof:', err);
      StateManager.setAlertAndOpen('Could not upload the proof: ' + (err.message || err), 'error');
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);

    const entry = {
      field,
      amount: parsed,
      previous: current || null,
      date: moment().format('YYYY-MM-DD'),
      by: StateManager.userID || null,
      byName: StateManager.userName || StateManager.userID || null,
      fileId: proof ? proof.id : null,
      fileName: proof ? proof.name : null,
      ...(isInitial ? { initial: true } : {}),
      ...(isOverride ? { override: true } : {}),
      // Credit rides on the entry, not the car: three reductions on one car can be
      // three different people's work. Only a reduction can pay, so only a reduction
      // carries a credit.
      ...(isReduction
        ? { credits: normalizeCredits(credits), credits_source: CREDIT_SOURCE.MANUAL }
        : {}),
    };

    // Read-modify-write rather than arrayUnion: entries are not unique, and this
    // matches how price_history is maintained elsewhere.
    const history = Array.isArray(car.nto_history) ? [...car.nto_history, entry] : [entry];
    const update = { [field]: parsed, nto_history: history };

    try {
      await firebase.firestore().doc(`cars/${stockNumber}`).set(update, { merge: true });

      // Only a change to the ORIGINAL NTO re-cuts the payable. That purchase doc is the
      // cost basis for every gross-profit, COGS, rep-margin and bonus-average figure in
      // the app, and sales performance is measured against the original NTO — so pricing
      // it at a renegotiated Updated NTO would quietly credit the reduction to profit and
      // to the rep's margin bonus, on top of the NTO-reduction commission they already
      // earn from `nto_history`.
      //
      // The consignor is still paid the renegotiated figure: writeCheck (Consignor.js),
      // the NTO scheduler, the payables list and the accounting summary all prefer
      // `car.updated_nto` over this amount.
      //
      // A sold car is never touched either — sold *means* the NTO is paid, so
      // re-creating the payable would flip the car back to `success` on the next
      // status cron.
      if (!isSold && field === 'nto') {
        const ref = firebase.firestore().doc(`purchases/${stockNumber}-NTO`);
        const snap = await ref.get();
        const nto = snap.exists ? snap.data() : {};
        const stillPayable = snap.exists ? (nto.isPayable == null || !!nto.isPayable) : true;
        if (stillPayable) {
          await ref.set({
            date: nto.date || moment().format('MM/DD/YYYY'),
            account: 'repair',
            memo: 'NTO',
            stock: stockNumber,
            amount: parsed,
            vendor: (car.consignor || {}).display_name || '',
            isPayable: true,
          }, { merge: true });
        }
      }

      StateManager.updateCar(update);
      StateManager.setAlertAndOpen(
        `${fieldLabel} ${isInitial ? 'set to' : 'updated to'} $${parsed.toLocaleString()}`,
        'success',
      );
      onClose();
    } catch (err) {
      console.error('Error updating NTO:', err);
      StateManager.setAlertAndOpen('Could not update the NTO: ' + (err.message || err), 'error');
    } finally {
      setSaving(false);
    }
  };

  const notice = isInitial
    ? 'This car has no NTO yet. Enter what the consignor agreed to — the consignment agreement is generated from this amount, and it creates the NTO payable. Proof is optional now; attach the signed agreement once you have it.'
    : isAdmin()
      ? (isOverride
        ? `Admin override — you are editing the ${fieldLabel} outside the normal rules for a ${car.status || 'this'} car. The history entry is marked as an override.`
        : 'As an admin you can edit either amount in any status. The NTO payable follows whichever amount we still owe.')
      : isSold
        ? 'This car is sold and the NTO is paid. The original NTO is locked — record the reduction as an Updated NTO.'
        : hasDeal
          ? 'This car has a deal, so the original NTO is locked. Record the renegotiated amount as an Updated NTO — the consignor is paid that figure, while sales performance still prices the car at the original NTO.'
          : 'Changing the NTO also updates the NTO payable. The consignor must have agreed to the new amount.';

  return (
    <StackedDialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {isInitial ? 'Set Original NTO' : `Update ${fieldLabel}`}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {stockNumber} — {[car.year, car.make, car.model].filter(Boolean).join(' ')}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity={isOverride ? 'warning' : 'info'} sx={{ mb: 2 }}>{notice}</Alert>

        {isAdmin() && !isInitial && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Amount to edit
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={field}
              onChange={(e, next) => { if (next) pickField(next); }}
              disabled={saving || uploading}
            >
              <ToggleButton value="nto">
                Original NTO{original ? ` · $${original.toLocaleString()}` : ''}
              </ToggleButton>
              <ToggleButton value="updated_nto">
                Updated NTO{car.updated_nto ? ` · $${Number(car.updated_nto).toLocaleString()}` : ''}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        <Box display="flex" gap={2} alignItems="flex-start" mb={2}>
          {!isInitial && (
            <TextField
              label="Current"
              size="small"
              value={current ? current.toLocaleString() : '—'}
              InputProps={{
                readOnly: true,
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ width: 160 }}
            />
          )}
          <TextField
            label={isInitial ? 'Original NTO' : `New ${fieldLabel}`}
            size="small"
            type="number"
            autoFocus
            value={amount}
            onChange={e => setAmount(e.target.value)}
            inputProps={{ min: 0 }}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{ width: 180 }}
          />
          {changed && !isInitial && (
            <Box pt={1}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600 }}
                color={delta > 0 ? 'success.dark' : 'error.main'}
              >
                {delta > 0 ? `Reduced by $${delta.toLocaleString()}` : `Increased by $${Math.abs(delta).toLocaleString()}`}
              </Typography>
            </Box>
          )}
        </Box>

        {isReduction && (
          <Box sx={{ mt: 2.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Who earns this reduction
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              The rep who negotiated it, not necessarily the consignment rep. Split it when
              two reps worked the consignor together.
            </Typography>
            <SalesCreditEditor value={credits} onChange={setCredits} disabled={saving} />
          </Box>
        )}

        <Box sx={{ mt: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Proof of the consignor&apos;s agreement{proofRequired ? '' : ' (optional)'}
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Button
              component="label"
              size="small"
              variant={proof || !proofRequired ? 'outlined' : 'contained'}
              startIcon={<AttachFileIcon />}
              disabled={uploading || saving}
            >
              {proof ? 'Replace Proof' : 'Attach Proof'}
              <input
                hidden
                type="file"
                accept="image/*,application/pdf"
                onChange={attachProof}
              />
            </Button>
            {proof && (
              <Chip
                size="small"
                color="success"
                icon={<CheckCircleIcon />}
                label={proof.name}
                onDelete={saving ? undefined : () => setProof(null)}
              />
            )}
            {uploading && (
              <Box sx={{ flex: 1, minWidth: 120 }}>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            Screenshot, email, or signed amendment. Saved to the car&apos;s Files and linked to this
            history entry.
            {proofRequired && !proof ? ' Required to save.' : ''}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || uploading || !canSave}
        >
          {saving ? 'Saving…' : (isInitial ? 'Set NTO' : 'Save Change')}
        </Button>
      </DialogActions>
    </StackedDialog>
  );
}
